import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'theme';
const THEME_SOURCE_KEY = 'themeSource'; // 'system' | 'user'
const prefersDarkQuery = '(prefers-color-scheme: dark)';

const resolveSystemTheme = () =>
  window.matchMedia && window.matchMedia(prefersDarkQuery).matches ? 'dark' : 'light';

export const ThemeProvider = ({ children }) => {
  const storedTheme = localStorage.getItem(THEME_KEY);
  const storedSource = localStorage.getItem(THEME_SOURCE_KEY);

  const initialTheme = storedTheme || resolveSystemTheme();
  const initialSource = storedSource || (storedTheme ? 'user' : 'system');

  const [theme, setTheme] = useState(initialTheme);
  const [themeSource, setThemeSource] = useState(initialSource);

  const updateStatusBarColor = useCallback((currentTheme) => {
    const metaThemeColor = document.querySelector('meta[name=theme-color]');
    const newColor = currentTheme === 'dark' ? '#212529' : '#F8F9FA';
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', newColor);
    }
  }, []);

  const applyTheme = useCallback(
    (nextTheme, source) => {
      setTheme(nextTheme);
      setThemeSource(source);
      localStorage.setItem(THEME_KEY, nextTheme);
      localStorage.setItem(THEME_SOURCE_KEY, source);
      updateStatusBarColor(nextTheme);
      document.documentElement.setAttribute('data-theme', nextTheme);
    },
    [updateStatusBarColor]
  );

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    applyTheme(newTheme, 'user');
  }, [applyTheme, theme]);

  // Первичная синхронизация состояния с localStorage и статус-баром
  useEffect(() => {
    applyTheme(theme, themeSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(prefersDarkQuery);

    const handleChange = (event) => {
      if (localStorage.getItem(THEME_SOURCE_KEY) === 'user') {
        return;
      }
      const nextTheme = event.matches ? 'dark' : 'light';
      applyTheme(nextTheme, 'system');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [applyTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
